<script setup>
import { computed } from 'vue';
import StarRating from 'shared/components/StarRating.vue';
import {
  CSAT_QUESTION_TYPES,
  EMOJI5_OPTIONS,
  EMOJI3_OPTIONS,
  THUMBS_OPTIONS,
  NPS_OPTIONS,
} from 'shared/constants/csatQuestionTypes';

const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: [Number, String, Array],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const selectedValue = computed(() => props.modelValue);

const isMultiChoice = computed(
  () =>
    props.question.type === CSAT_QUESTION_TYPES.CHOICE &&
    props.question.multiple
);

const checkboxSelections = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
);

const optionText = opt => (typeof opt === 'string' ? opt : (opt?.text ?? ''));

const handleSelect = value => {
  if (props.disabled) return;
  emit('update:modelValue', value);
};

const handleStarSelect = value => {
  handleSelect(value);
};

const handleCommentInput = event => {
  emit('update:modelValue', event.target.value);
};

const toggleCheckbox = option => {
  if (props.disabled) return;
  const text = optionText(option);
  const current = new Set(checkboxSelections.value);
  if (current.has(text)) {
    current.delete(text);
  } else {
    current.add(text);
  }
  emit('update:modelValue', Array.from(current));
};

const isOptionSelected = option => {
  const text = optionText(option);
  if (isMultiChoice.value) {
    return checkboxSelections.value.includes(text);
  }
  return props.modelValue === text;
};
</script>

<template>
  <div class="flex flex-col gap-3 py-3">
    <label class="text-sm font-medium text-n-slate-12 leading-snug normal-case">
      {{ question.text }}
      <span v-if="question.required" class="text-n-ruby-9">*</span>
    </label>

    <StarRating
      v-if="question.type === CSAT_QUESTION_TYPES.STARS"
      :selected-rating="Number(selectedValue) || 0"
      :is-disabled="disabled"
      class="!justify-start !px-0"
      @select-rating="handleStarSelect"
    />

    <div
      v-else-if="question.type === CSAT_QUESTION_TYPES.EMOJI5"
      class="flex justify-start gap-4"
    >
      <button
        v-for="option in EMOJI5_OPTIONS"
        :key="option.value"
        type="button"
        :disabled="disabled"
        class="text-3xl outline-none transition-all duration-200"
        :class="[
          selectedValue === option.value
            ? 'grayscale-0 scale-110'
            : 'grayscale hover:grayscale-0 hover:scale-110',
          disabled ? 'cursor-not-allowed opacity-60' : '',
        ]"
        @click="handleSelect(option.value)"
      >
        {{ option.emoji }}
      </button>
    </div>

    <div
      v-else-if="question.type === CSAT_QUESTION_TYPES.EMOJI3"
      class="flex justify-start gap-6"
    >
      <button
        v-for="option in EMOJI3_OPTIONS"
        :key="option.value"
        type="button"
        :disabled="disabled"
        class="text-3xl outline-none transition-all duration-200"
        :class="[
          selectedValue === option.value
            ? 'grayscale-0 scale-110'
            : 'grayscale hover:grayscale-0 hover:scale-110',
          disabled ? 'cursor-not-allowed opacity-60' : '',
        ]"
        @click="handleSelect(option.value)"
      >
        {{ option.emoji }}
      </button>
    </div>

    <div
      v-else-if="question.type === CSAT_QUESTION_TYPES.THUMBS"
      class="flex justify-start gap-3"
    >
      <button
        v-for="option in THUMBS_OPTIONS"
        :key="option.value"
        type="button"
        :disabled="disabled"
        class="w-12 h-12 rounded-xl flex items-center justify-center border border-n-weak outline-none transition-transform"
        :class="[
          option.colorClass,
          selectedValue === option.value
            ? 'scale-110 bg-n-alpha-1'
            : 'opacity-70 hover:opacity-100 hover:scale-105',
          disabled ? 'cursor-not-allowed opacity-50' : '',
        ]"
        @click="handleSelect(option.value)"
      >
        <i :class="option.iconClass" class="size-6" />
      </button>
    </div>

    <div
      v-else-if="question.type === CSAT_QUESTION_TYPES.NPS"
      class="flex flex-wrap gap-1.5"
    >
      <button
        v-for="option in NPS_OPTIONS"
        :key="option.value"
        type="button"
        :disabled="disabled"
        class="min-w-9 h-9 px-2 rounded-md text-sm font-semibold border transition-colors"
        :class="[
          selectedValue === option.value
            ? 'bg-n-brand text-white border-n-brand'
            : 'bg-n-solid-1 text-n-slate-12 border-n-weak hover:bg-n-alpha-1',
          disabled ? 'cursor-not-allowed opacity-60' : '',
        ]"
        @click="handleSelect(option.value)"
      >
        {{ option.value }}
      </button>
    </div>

    <textarea
      v-else-if="question.type === CSAT_QUESTION_TYPES.COMMENT"
      rows="3"
      :value="selectedValue || ''"
      :placeholder="question.placeholder || ''"
      :disabled="disabled"
      class="w-full rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 text-sm p-3 focus:ring-0 focus:outline-n-brand disabled:opacity-60"
      @input="handleCommentInput"
    />

    <div
      v-else-if="question.type === CSAT_QUESTION_TYPES.CHOICE"
      class="flex flex-col gap-2"
    >
      <label
        v-for="(option, index) in question.options || []"
        :key="`${question.id}-${index}`"
        class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer normal-case"
        :class="{ 'cursor-not-allowed opacity-60': disabled }"
      >
        <input
          v-if="isMultiChoice"
          type="checkbox"
          :checked="isOptionSelected(option)"
          :disabled="disabled"
          class="reset-base w-4 h-4 shrink-0 accent-n-brand cursor-pointer"
          @change="toggleCheckbox(option)"
        />
        <input
          v-else
          type="radio"
          :name="`question-${question.id}`"
          :checked="isOptionSelected(option)"
          :disabled="disabled"
          class="reset-base w-4 h-4 shrink-0 accent-n-brand cursor-pointer"
          @change="handleSelect(optionText(option))"
        />
        {{ optionText(option) }}
      </label>
    </div>
  </div>
</template>
